import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import RobustSelect from '../../components/RobustSelect';
import { SelectItem } from '../../components/ui/select';
import { Card } from '../../components/ui/card';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

function GenerateAIQuestions() {
  const navigate = useNavigate();
  const location = useLocation();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    bank_id: location.state?.bankId || 'none',
    topic: '',
    num_questions: 5,
    difficulty: 'medium',
  });

  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    try {
      const response = await api.get('/question-banks');
      setBanks(response.data);
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/questions/generate-ai', formData);
      toast.success(response.data.message);
      navigate(`/admin/question-banks/${formData.bank_id}`);
    } catch (error) {
      console.error('Error generating questions:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate questions');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="w-8 h-8 text-purple-600" />
            <h1 className="text-3xl font-bold text-gray-900" data-testid="ai-generate-title">
              AI Question Generator
            </h1>
          </div>
          <p className="text-gray-600">Generate MCQ questions automatically using AI</p>
        </div>

        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="bank_id">Question Bank *</Label>
              <RobustSelect
                value={formData.bank_id}
                onValueChange={(value) => setFormData({ ...formData, bank_id: value })}
                placeholder="Select a bank"
                testId="bank-select"
              >
                <SelectItem value="none" disabled>Select a question bank</SelectItem>
                {banks.map((bank) => (
                  <SelectItem key={bank.id} value={bank.id}>
                    {bank.title}
                  </SelectItem>
                ))}
              </RobustSelect>
            </div>

            <div className="space-y-2">
              <Label htmlFor="topic">Topic *</Label>
              <Input
                id="topic"
                data-testid="topic-input"
                value={formData.topic}
                onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                placeholder="e.g., Photosynthesis, World War II, Python Programming"
                required
              />
              <p className="text-sm text-gray-500">
                Enter the subject or topic for question generation
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="num_questions">Number of Questions *</Label>
                <Input
                  id="num_questions"
                  data-testid="num-questions-input"
                  type="number"
                  min="1"
                  max="20"
                  value={formData.num_questions}
                  onChange={(e) => setFormData({ ...formData, num_questions: parseInt(e.target.value) })}
                  required
                />
                <p className="text-sm text-gray-500">Maximum 20 questions per request</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="difficulty">Difficulty Level *</Label>
                <Select
                  value={formData.difficulty}
                  onValueChange={(value) => setFormData({ ...formData, difficulty: value })}
                >
                  <SelectTrigger data-testid="difficulty-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <p className="font-medium mb-1">AI-Powered Generation</p>
                  <p>
                    Questions will be automatically generated with 4 multiple choice options,
                    correct answers, and explanations. You can review and edit them after generation.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-purple-600 hover:bg-purple-700"
                disabled={loading}
                data-testid="generate-button"
              >
                {loading ? (
                  <>
                    <span className="animate-spin mr-2">⚡</span>
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Questions
                  </>
                )}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Layout>
  );
}

export default GenerateAIQuestions;
